import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  Our_Product_Bg,
  Technology_Complaint,
  Technology_Deployment,
  Technology_Engine,
} from "../../assets";
import { useModal } from "../../utils/demoProvider";
import { useLocation, Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../utils/animations";
const helmetData = new HelmetData({});

export default function TechnologyPage() {
  const { openModal } = useModal();

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".tech-hero-heading"), {
          scrollTrigger: revealTrigger(".tech-hero-heading"),
        });
        riseFrom(".tech-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".tech-hero-item", "top 85%"),
        });
        riseFrom(".tech-infra-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tech-infra-copy"),
        });
        riseFrom(".tech-infra-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".tech-infra-media"),
        });
        riseFrom(".tech-engine-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tech-engine-copy"),
        });
        riseFrom(".tech-engine-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".tech-engine-media"),
        });
        riseFrom(".tech-dlm-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tech-dlm-copy"),
        });
        riseFrom(".tech-dlm-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".tech-dlm-media"),
        });
        riseFrom(".tech-cta-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".tech-cta-head"),
        });
        riseFrom(".tech-cta-btn", {
          delay: 0.2,
          scrollTrigger: revealTrigger(".tech-cta-btn", "top 90%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Technology — AI Infrastructure &amp; Analytics Engine | [AI]</title>
        <link rel="canonical" href="https://analyticsintelligence.com/technology" />
        <meta
          name="description"
          content="The Analytics Intelligence technology stack — secure AI infrastructure, an AI-driven Analytics Engine, and a Data Language Model for explainable, deployable analytics reasoning."
        />
        <meta
          name="keywords"
          content="AI infrastructure, MCP server, PLM, Analytics Engine, Data Language Model, DLM, agentic workflows, RAG, explainable AI, secure enterprise AI, Analytics Intelligence"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="Technology — AI Infrastructure & Analytics Engine | [AI]" />
        <meta property="og:description" content="Secure AI infrastructure, an AI-driven Analytics Engine, and a Data Language Model — explainable, scalable AI built for deployment by Analytics Intelligence." />
        <meta property="og:url" content="https://analyticsintelligence.com/technology" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="Technology — AI Infrastructure & Analytics Engine | [AI]" />
        <meta name="twitter:description" content="Secure AI infrastructure, an AI-driven Analytics Engine, and a Data Language Model — explainable, scalable AI built for deployment by Analytics Intelligence." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Technology — AI Infrastructure & Analytics Engine | Analytics Intelligence",
            "description": "The Analytics Intelligence technology stack: secure AI infrastructure with an MCP server and PLM, an AI-driven Analytics Engine, and a Data Language Model (DLM) for explainable analytics reasoning and agentic workflows.",
            "url": "https://analyticsintelligence.com/technology",
            "publisher": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            }
          }
        `}</script>
      </Helmet>

      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[95vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex flex-col items-center gap-[32px]">
              <div className="flex flex-col gap-4 items-center">
                <span className="tech-hero-item text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Technology
                </span>
                <h2
                  className="tech-hero-heading text-[38px] md:text-[48px] text-center font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      "Infrastructure, analytics, and agents—secure, explainable, and scalable AI built for deployment.",
                  }}
                />
              </div>

              <div className="tech-hero-item flex justify-center items-center gap-4 md:gap-8">
                <a href="#infrastructure">
                  <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                    Explore Use Cases
                  </button>
                </a>
                <Link to="/contact">
                  <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-white text-poppingBlue rounded-[100px]">
                    Talk to Sales
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative pt-[48px] pb-[96px]"
          id="infrastructure"
        >
          <div className="container flex flex-col-reverse md:flex-row items-center mt-11 gap-8">
            <div className="basis-2/4 flex items-center justify-center">
              <img
                src={Technology_Complaint}
                alt="Data Structures"
                className="tech-infra-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="tech-infra-copy basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-bold text-white text-center md:text-left">
                Infrastructure:
              </h2>
              <h2 class="text-lg md:text-[24px] font-bold text-white text-center md:text-left">
                Secure Data Infrastructure for AI-Native Analytics
              </h2>

              <p className="text-sm md:text-base text-white text-center md:text-left">
                Our infrastructure unifies{" "}
                <span className="text-[#88BEFF] font-extrabold">
                  enterprise behavioural data, market data, and operational data
                </span>{" "}
                into a secure AI pipeline optimised for LLM applications, RAG
                systems, and agentic workflows. Powered by our MCP Server,
                enterprise data is contextualised, governed, and structured for
                reasoning while our PLM provides private model intelligence
                within controlled cloud environments.
              </p>

              <p className="text-sm md:text-base text-white text-center md:text-left">
                This foundation ensures end-to-end trust, privacy, contextual
                depth, and secure activation of enterprise data.
              </p>
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative py-[48px]"
          id="analytics-engine"
        >
          <div className="container relative flex flex-col">
            <div className="w-[150px] lg:w-[600px] h-[300px] bg-[#0073FF]/30 blur-[130px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
            <div className="container flex flex-col md:flex-row items-center mt-11 gap-8 relative z-[100]">
              <div className="tech-engine-copy basis-2/4 space-y-4">
                <h2 class="text-2xl md:text-[32px] font-bold text-white text-center md:text-left">
                  Analytics Engine:
                </h2>

                <h2 class="text-lg md:text-[24px] font-bold text-white text-center md:text-left">
                  AI-Driven Analytical Reasoning
                </h2>

                <div className="space-y-2 text-center md:text-left">
                  <p className="text-sm md:text-base text-white">
                    Our Analytics Engine is purpose-built for the AI era, not
                    inherited from legacy BI systems. It uses machine learning
                    and model-driven reasoning to{" "}
                    <span className="text-[#88BEFF] font-extrabold">
                      dentify patterns, correlations, anomalies, outliers,
                      signal clusters, and opportunity surfaces in data
                    </span>
                    . It does this without requiring dashboards or manual
                    interpretation. The engine executes analytical logic
                    securely at scale, powering both autonomous agentic
                    workflows and conversational insight delivery.
                  </p>
                </div>
              </div>

              <div className="basis-2/4 flex items-center justify-center">
                <img
                  src={Technology_Engine}
                  alt="Data Structures"
                  className="tech-engine-media w-full md:max-w-[400px] h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative pt-[48px] pb-[96px]"
          id="agent-framework"
        >
          <div className="container flex flex-col-reverse md:flex-row items-center mt-11 gap-8">
            <div className="basis-2/4 flex items-center justify-center">
              <img
                src={Technology_Deployment}
                alt="Data Structures"
                className="tech-dlm-media w-full md:max-w-[500px] h-auto"
              />
            </div>

            <div className="tech-dlm-copy basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-bold text-white text-center md:text-left">
                Data Language Model (DLM):
              </h2>

              <h2 class="text-lg md:text-[24px] font-bold text-white text-center md:text-left">
                Model Intelligence Optimised for Analytics
              </h2>

              <p className="text-sm md:text-base text-white text-center md:text-left md:text-white">
                Our Data Language Model is fine-tuned small language model
                specifically for analytics reasoning. It understands enterprise
                metrics, behavioural signals, financial structures, and domain
                terminology, enabling the system to interpret intent accurately
                and produce context-aware insight responses.
              </p>
              <p className="text-sm md:text-base text-white text-center md:text-left md:text-white">
                The DLM works in tandem with PLM and the Analytics Engine to
                ensure every query, prompt, and instruction is understood
                precisely and mapped to the correct analytical process.
              </p>
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative pt-[96px] pb-[116px]"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="container flex flex-col items-center gap-6">
            <div className="tech-cta-head">
              <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white">
                Ready to See It in Action?
              </h2>
              <p className="text-sm md:text-base text-center text-[#FFFFFF]">
                Explore how our secure AI infrastructure and agentic platform
                can transform your analytics capabilities.
              </p>
            </div>

            <button
              onClick={openModal}
              className="tech-cta-btn text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
            >
              Request a Demo
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
